import type { LoyaltySettingsVersionDto, LoyaltyTermsDto } from '@deli/contracts';
import { addDays, addMonths, businessDate, startOfBusinessDay } from '@deli/time';
import { useState, type FormEvent } from 'react';
import {
  useLoyaltyMovement,
  useLoyaltyOutstanding,
  useLoyaltySettings,
  useUpdateLoyaltySettings,
} from '../api/loyalty.js';
import { ErrorText } from '../components/ErrorText.js';
import { QueryState } from '../components/QueryState.js';
import { formatDateTime, formatMoney, parseMoneyInput, toMoneyInput } from '../format.js';
import { t } from '../i18n/uk.js';

const tl = t.loyalty;

interface Form {
  enabled: boolean;
  accrualPercent: string;
  pointValue: string;
  maxRedeemPercent: string;
  expiryMonths: string;
  reason: string;
}

const formOf = (v: LoyaltySettingsVersionDto): Form => ({
  enabled: v.enabled,
  accrualPercent: String(v.accrualPercent).replace('.', ','),
  pointValue: toMoneyInput(v.pointValue),
  maxRedeemPercent: v.maxRedeemPercent === null ? '' : String(v.maxRedeemPercent).replace('.', ','),
  expiryMonths: v.expiryMonths === null ? '' : String(v.expiryMonths),
  reason: '',
});

const percent = (text: string) => Number(text.trim().replace(',', '.'));

/** The form as API terms; null when a number doesn't parse. */
function termsOf(form: Form): LoyaltyTermsDto | null {
  const pointValue = parseMoneyInput(form.pointValue);
  const accrual = percent(form.accrualPercent);
  const cap = form.maxRedeemPercent.trim() === '' ? null : percent(form.maxRedeemPercent);
  const months = form.expiryMonths.trim() === '' ? null : Number(form.expiryMonths.trim());
  if (pointValue === null || Number.isNaN(accrual) || Number.isNaN(cap) || Number.isNaN(months)) {
    return null;
  }
  return {
    enabled: form.enabled,
    accrualPercent: accrual,
    pointValue,
    maxRedeemPercent: cap,
    expiryMonths: months,
  };
}

function SettingsForm({
  current,
  onSaved,
}: {
  current: LoyaltySettingsVersionDto;
  onSaved: (message: string) => void;
}) {
  const update = useUpdateLoyaltySettings();
  const [form, setForm] = useState<Form>(formOf(current));
  const [checked, setChecked] = useState(false);
  const terms = termsOf(form);
  const set = (next: Partial<Form>) => {
    setForm({ ...form, ...next });
    setChecked(false);
  };

  const send = (dryRun: boolean) => (event?: FormEvent) => {
    event?.preventDefault();
    if (!terms) return;
    update.mutate(
      { ...terms, reason: form.reason.trim(), dryRun },
      {
        onSuccess: (change) => {
          setChecked(dryRun);
          // The form starts again from the new version, so the confirmation lives on the page.
          if (change.applied && change.version) onSaved(tl.applied(change.version.version));
        },
      },
    );
  };
  const result = update.data;

  return (
    <form className="card" onSubmit={send(false)}>
      <h2>{tl.settingsTitle}</h2>
      <p className="muted hint">{tl.intro}</p>
      <label className="check">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => set({ enabled: e.target.checked })}
        />
        {tl.enabled}
      </label>
      <div className="form-grid">
        <label>
          {tl.accrualPercent}
          <input
            aria-label={tl.accrualPercent}
            inputMode="decimal"
            value={form.accrualPercent}
            onChange={(e) => set({ accrualPercent: e.target.value })}
          />
        </label>
        <label>
          {tl.pointValue}
          <input
            aria-label={tl.pointValue}
            inputMode="decimal"
            value={form.pointValue}
            onChange={(e) => set({ pointValue: e.target.value })}
          />
        </label>
        <label>
          {tl.maxRedeemPercent}
          <input
            aria-label={tl.maxRedeemPercent}
            inputMode="decimal"
            placeholder={tl.noCap}
            value={form.maxRedeemPercent}
            onChange={(e) => set({ maxRedeemPercent: e.target.value })}
          />
        </label>
        <label>
          {tl.expiryMonths}
          <input
            aria-label={tl.expiryMonths}
            inputMode="numeric"
            placeholder={tl.noExpiry}
            value={form.expiryMonths}
            onChange={(e) => set({ expiryMonths: e.target.value })}
          />
        </label>
      </div>
      <div className="inline-form">
        <input
          aria-label={tl.reason}
          placeholder={tl.reason}
          className="grow"
          value={form.reason}
          onChange={(e) => set({ reason: e.target.value })}
        />
        <button
          type="button"
          disabled={!terms || !form.reason.trim() || update.isPending}
          onClick={() => send(true)()}
        >
          {tl.check}
        </button>
        <button
          type="submit"
          className="primary"
          disabled={!terms || !form.reason.trim() || !checked || update.isPending}
        >
          {tl.apply}
        </button>
      </div>
      {!terms && <p className="error-text">{t.common.invalidAmount}</p>}
      {result && !result.applied && (
        <p className="muted" role="status">
          {result.version
            ? tl.unchanged
            : result.conversion
              ? tl.conversion(
                  result.conversion.customers,
                  result.conversion.pointsBefore,
                  result.conversion.pointsAfter,
                )
              : tl.noConversion}
        </p>
      )}
      <ErrorText error={update.error} />
    </form>
  );
}

function History({ versions }: { versions: LoyaltySettingsVersionDto[] }) {
  return (
    <div className="card">
      <h2>{tl.historyTitle}</h2>
      <table className="table">
        <thead>
          <tr>
            <th>{tl.version}</th>
            <th>{tl.from}</th>
            <th />
            <th>{tl.accrualPercent}</th>
            <th>{tl.pointValue}</th>
            <th>{tl.maxRedeemPercent}</th>
            <th>{tl.expiryMonths}</th>
            <th>{tl.by}</th>
            <th>{tl.reason}</th>
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => (
            <tr key={v.id}>
              <td>{v.version}</td>
              <td>{formatDateTime(v.validFrom)}</td>
              <td>
                <span className={`badge ${v.enabled ? 'ok' : ''}`}>
                  {v.enabled ? tl.on : tl.off}
                </span>
              </td>
              <td className="num">{v.accrualPercent}%</td>
              <td className="num">{formatMoney(v.pointValue)}</td>
              <td className="num">
                {v.maxRedeemPercent === null ? tl.noLimit : `${v.maxRedeemPercent}%`}
              </td>
              <td>{v.expiryMonths === null ? tl.never : tl.months(v.expiryMonths)}</td>
              <td>{v.createdByName ?? t.audit.system}</td>
              <td className="muted">{v.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Reports by Kyiv calendar days and months (spec §9), whatever the browser's time zone. */
function Reports() {
  const [asOf, setAsOf] = useState('');
  const [month, setMonth] = useState(businessDate(new Date()).slice(0, 7));
  // "As of a day" = the end of that Kyiv day.
  const endOfDay = asOf
    ? new Date(startOfBusinessDay(addDays(asOf, 1)).getTime() - 1).toISOString()
    : null;
  const outstanding = useLoyaltyOutstanding(endOfDay);
  const from = startOfBusinessDay(`${month}-01`);
  const to = startOfBusinessDay(addMonths(`${month}-01`, 1));
  const movement = useLoyaltyMovement(from.toISOString(), to.toISOString());
  return (
    <div className="card">
      <h2>{tl.reportsTitle}</h2>
      <h3>{tl.outstanding}</h3>
      <label>
        {tl.asOf}{' '}
        <input
          type="date"
          aria-label={tl.asOf}
          value={asOf}
          onChange={(e) => setAsOf(e.target.value)}
        />{' '}
        {!asOf && <span className="muted">{tl.now}</span>}
      </label>
      <QueryState query={outstanding}>
        {(o) => (
          <dl className="totals">
            <dt>{tl.active}</dt>
            <dd>{tl.pointsWorth(o.active, formatMoney(o.activeValue))}</dd>
            <dt>{tl.pending}</dt>
            <dd>{tl.pointsWorth(o.pending, formatMoney(o.pendingValue))}</dd>
            <dt>{tl.customersWith}</dt>
            <dd>{o.customers}</dd>
          </dl>
        )}
      </QueryState>
      <h3>{tl.movement}</h3>
      <label>
        {tl.month}{' '}
        <input
          type="month"
          aria-label={tl.month}
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
      </label>
      <QueryState query={movement}>
        {(m) =>
          m.rows.length === 0 ? (
            <p className="muted">{tl.noMovement}</p>
          ) : (
            <table className="table">
              <tbody>
                {m.rows.map((r) => (
                  <tr key={`${r.kind}-${r.sign}`}>
                    <td>{tl.kinds[r.kind]}</td>
                    <td className="num">
                      {r.points > 0 ? '+' : ''}
                      {r.points}
                    </td>
                    <td className="muted">
                      {tl.entries}: {r.entries}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </QueryState>
    </div>
  );
}

/** Admin → Q-бали (LOY-03, LOY-11). */
export function LoyaltyPage() {
  const settings = useLoyaltySettings();
  const [saved, setSaved] = useState<string | null>(null);
  return (
    <section className="wide">
      <h1>{tl.title}</h1>
      {saved && (
        <p className="badge ok" role="status">
          {saved}
        </p>
      )}
      <QueryState query={settings}>
        {(versions) => (
          <>
            {/* Keyed by the newest version: the form starts from what is in force. */}
            <SettingsForm key={versions[0]!.id} current={versions[0]!} onSaved={setSaved} />
            <History versions={versions} />
          </>
        )}
      </QueryState>
      <Reports />
    </section>
  );
}
